import type { EthogramTerm } from '../lib/api/endpoints.js';

interface Props {
  terms: EthogramTerm[];
  selected: string[];
  onChange: (terms: string[]) => void;
  /**
   * `context` renders selections teal, `signal` amber. Not decoration: it is
   * the project's argument, made by the interface. The antecedent and the
   * consequence are the things no other corpus keeps.
   */
  tone: 'context' | 'signal';
  /** Single-select for meaning; multi for everything else. */
  single?: boolean;
  /** How many to show before "more". Keeps the first screen scannable. */
  initialVisible?: number;
  labelledBy?: string;
}

/**
 * Picks terms from a server-delivered vocabulary.
 *
 * Note there is no hardcoded list anywhere in this component. Terms arrive from
 * `/v1/ethograms`, and the definition is rendered as the `title` so a
 * contributor is never guessing what a term means — which is what makes the
 * difference between coded data and noise.
 */
export function TermPicker({
  terms,
  selected,
  onChange,
  tone,
  single = false,
  initialVisible = 8,
  labelledBy,
}: Props) {
  // Terms already chosen always render, even when they sit past the fold —
  // hiding a selection behind "show more" is how people lose track of what
  // they picked.
  const visible = terms.filter((t, i) => i < initialVisible || selected.includes(t.term));
  const hidden = terms.filter((t, i) => i >= initialVisible && !selected.includes(t.term));

  const toggle = (term: string) => {
    if (single) {
      onChange(selected.includes(term) ? [] : [term]);
      return;
    }
    onChange(
      selected.includes(term) ? selected.filter((t) => t !== term) : [...selected, term],
    );
  };

  return (
    <div>
      <div className={`chips chips--${tone}`} role="group" aria-labelledby={labelledBy}>
        {visible.map((term) => (
          <button
            key={term.term}
            type="button"
            className="chip"
            aria-pressed={selected.includes(term.term)}
            title={term.definition}
            onClick={() => toggle(term.term)}
          >
            {term.label}
          </button>
        ))}
      </div>

      {hidden.length > 0 && (
        <details style={{ marginTop: '0.6rem' }}>
          <summary
            className="meta"
            style={{ cursor: 'pointer', listStyle: 'revert' }}
          >
            {hidden.length} more
          </summary>
          <div className={`chips chips--${tone}`} style={{ marginTop: '0.5rem' }}>
            {hidden.map((term) => (
              <button
                key={term.term}
                type="button"
                className="chip"
                aria-pressed={false}
                title={term.definition}
                onClick={() => toggle(term.term)}
              >
                {term.label}
              </button>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
